import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import {
  buildFocusedPlan,
  planLeaves,
  selectFocusedSteps,
} from '@shared/assessment-definition/dist/src/focused-plan'
import type {
  FocusedPlan,
  PlanDefinition,
  PlanPanel,
} from '@shared/assessment-definition/dist/src/focused-plan'
import type { GuidedFocusedPage, useGuidedModeStore } from '../stores/guidedMode'
import type {
  GuidedAnswer,
  GuidedAssessmentPath,
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeSymptom,
} from '../types/guided-mode.types'
import { assessmentQuestionSteps } from '../utils/assessment-definition'
import { DANGER_SIGNS_GROUP_ID } from '../utils/danger-sign-screen'
import {
  buildCalculatedDateStepDependencies,
  includeCalculatedDateDependents,
} from '../utils/calculated-date'
import { isStepVisible, stepKey } from '../utils/step-engine'
import { selectGuidedFocusedOptions } from '../services/guided-escalation.service'
import { availableSymptoms, recordedEntry } from '../utils/symptoms'
import { useGuidedEntryPointRules } from './useGuidedEntryPointRules'

const mrxEngine = () => require('@/utils/mrxEngine') as typeof import('@/utils/mrxEngine')

export const FOCUSED_SYMPTOM_STEP_PREFIX = 'focused.symptom.'
const VITAL_SIGNS_TITLE_TOKEN = 'group.vital_signs.prompt'

interface FocusedEvaluationResult {
  comprehensive?: boolean
  panels?: PlanPanel[]
}

interface FocusedEvaluationOptions {
  commitRules?: boolean
  triggerStepId?: string
}

interface UseGuidedFocusedPlanOptions {
  store: ReturnType<typeof useGuidedModeStore>
  answers: Ref<Record<string, GuidedAnswer>>
  activeAssessmentPath: Ref<GuidedAssessmentPath | null>
  definition: ComputedRef<GuidedModeDefinition | null>
  excludedCodes: ComputedRef<string[]>
  escalated?: ComputedRef<boolean>
  reportError?: (error: unknown) => void
}

function leafSteps(definition: GuidedModeDefinition): GuidedModeStep[] {
  return (definition.assessment?.steps || []).flatMap(step => (step.kind === 'group' ? step.steps || [] : [step]))
}

export function useGuidedFocusedPlan({
  store, answers, activeAssessmentPath, definition, excludedCodes, escalated, reportError,
}: UseGuidedFocusedPlanOptions) {
  const evaluating = ref(false)
  let evaluationRun = 0
  let evaluatedKey: string | null = null
  let membershipDefinition: GuidedModeDefinition | null = null
  let membershipContent = store.clinicalContent
  const optionMemberships = new Map<string, Promise<string[]>>()
  const active = computed(() => store.consultationPath === 'focused' && Boolean(definition.value))
  const trackSymptoms = computed<GuidedModeSymptom[]>(() => (
    activeAssessmentPath.value ? store.clinicalContent.symptoms?.[activeAssessmentPath.value] || [] : []
  ))
  const planSymptoms = computed<GuidedModeSymptom[]>(() => availableSymptoms(trackSymptoms.value, answers.value)
    .map(symptom => {
      const entry = recordedEntry(symptom, answers.value) || symptom
      return { ...symptom, code: entry.code, conceptId: entry.conceptId, value: entry.value }
    }))
  const symptomById = computed(() => new Map(planSymptoms.value.map(symptom => [symptom.id, symptom])))
  const recordedConceptIds = computed(() => new Set(store.startedFocusedAssessments
    .filter(symptom => !symptom.selectionOnly && !store.assumedSymptomAnswers[symptom.id])
    .map(symptom => symptom.conceptId)))
  const stepById = computed(() => new Map((definition.value ? leafSteps(definition.value) : [])
    .flatMap(step => (step.id ? [[step.id, step] as const] : []))))
  const groupByStepId = computed(() => new Map((definition.value?.assessment?.steps || [])
    .flatMap(group => (group.kind === 'group' ? group.steps || [] : [])
      .flatMap(step => (step.id ? [[step.id, group] as const] : [])))))
  const calculatedDateStepDependencies = computed(() => (
    buildCalculatedDateStepDependencies(definition.value)
  ))
  const planLeafEntries = computed(() => (definition.value
    ? planLeaves(definition.value as PlanDefinition)
    : []))
  const leafIdByConceptId = computed(() => new Map(planLeafEntries.value.flatMap(leaf => {
    const step = stepById.value.get(leaf.id)
    const conceptId = step?.questionId ? definition.value?.questions[step.questionId]?.conceptId : undefined
    return conceptId ? [[conceptId, leaf.id] as const] : []
  })))
  const entryPointRules = useGuidedEntryPointRules({
    store,
    answers,
    activeAssessmentPath,
    definition,
    excludedCodes,
    symptomById,
    stepById,
    planLeafEntries,
    leafIdByConceptId,
  })
  const {
    values,
    recordedEntryPointIds,
    activeAssumeIds,
    activeAskIds,
    askStepIds,
    selectedSymptomIds,
    triggerExclusions,
  } = entryPointRules

  const assessmentSteps = computed(() => (
    assessmentQuestionSteps(definition.value, excludedCodes.value)
  ))
  const upfrontSteps = computed<GuidedModeStep[]>(() => {
    if (!definition.value || !['newborn', 'child'].includes(activeAssessmentPath.value || '')) {
      return []
    }
    const dangerSigns = assessmentSteps.value.find(step => step.id === DANGER_SIGNS_GROUP_ID)
      || assessmentSteps.value.find(step => step.id === 'group.flexi_danger_signs')
    if (!dangerSigns) return []

    const preparationCodes = new Set([
      'temperature', 'respiratory_rate', 'heart_rate', 'oxygen_saturation',
      'recent_fever', 'recent_cool_cold_touch', 'fever_duration', 'fever_seven_days',
    ])
    const isPreparation = (step: GuidedModeStep) => {
      const question = step.questionId ? definition.value!.questions[step.questionId] : undefined
      const code = question && definition.value!.concepts[question.conceptId]?.code?.code
      return Boolean(code && preparationCodes.has(code))
    }
    const preparation = assessmentSteps.value.flatMap(step => {
      if (step.kind !== 'group') return isPreparation(step) ? [step] : []
      const children = (step.steps || []).filter(isPreparation)
      return children.length ? [{ ...step, steps: children }] : []
    })
    return [...preparation, dangerSigns]
  })
  const upfrontStepIds = computed(() => new Set(upfrontSteps.value.flatMap(step => (
    step.kind === 'group' ? (step.steps || []).flatMap(leaf => leaf.id || []) : step.id || []
  ))))

  const plan = computed<FocusedPlan | null>(() => {
    if (!active.value || !definition.value || !activeAssessmentPath.value) return null
    return buildFocusedPlan({
      definition: definition.value as PlanDefinition,
      symptoms: planSymptoms.value,
      selectedSymptomIds: selectedSymptomIds.value,
      answeredSymptomIds: recordedEntryPointIds.value,
      vitalSignRules: [],
      triggerExclusions: triggerExclusions.value,
      values: values.value,
      focusedStepIds: [
        ...(escalated?.value
          ? leafSteps(definition.value).flatMap(step => step.id || [])
          : [...(store.focusedStepIds || []), ...store.keptEscalationStepIds]),
        ...upfrontStepIds.value,
      ],
      excludedCodes: excludedCodes.value,
    })
  })

  const plannedSteps = computed<GuidedModeStep[] | null>(() => {
    if (!plan.value || !definition.value) return null
    const leaves = (ids: string[]) => ids.flatMap(id => stepById.value.get(id) || [])
    const withCalculatedDateOutputs = (stepIds: string[]) => (
      includeCalculatedDateDependents(stepIds, calculatedDateStepDependencies.value)
    )
    const leafAnswered = (stepId: string) => {
      const step = stepById.value.get(stepId)
      const conceptId = step?.questionId ? definition.value!.questions[step.questionId]?.conceptId : undefined
      const answer = conceptId ? answers.value[conceptId] : undefined
      return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
    }
    const triggered = new Set([
      ...activeAssumeIds.value, ...activeAskIds.value,
      ...recordedEntryPointIds.value.filter(id => !store.recordedSymptomIds.includes(id)),
    ])
    const mustConfirm = new Set(askStepIds.value)
    const introduced = new Set(store.introducedFocusedStepIds)
    const answeredWaveStepIds = new Set<string>()
    const stepsToAsk = (stepIds: string[], dynamic: boolean) => stepIds.filter(stepId => {
      if (!dynamic || introduced.has(stepId) || !leafAnswered(stepId) || mustConfirm.has(stepId)) {
        return true
      }
      answeredWaveStepIds.add(stepId)
      return false
    })
    const withGroupGate = (step: GuidedModeStep): GuidedModeStep => {
      const gate = groupByStepId.value.get(step.id!)?.visibleWhen
      if (!gate) return step
      return { ...step, visibleWhen: step.visibleWhen ? { all: [gate, step.visibleWhen] } : gate }
    }
    const symptomPages = (symptom: GuidedModeSymptom, stepIds: string[]): GuidedModeStep[] => {
      type Page = { checklist?: GuidedModeStep; groups: Set<GuidedModeStep | undefined>; stepIds: string[]; title: string }
      const pages: Page[] = []
      for (const stepId of stepIds) {
        const group = groupByStepId.value.get(stepId)
        const checklist = group?.multiselect ? group : undefined
        const title = group?.titleToken || symptom.labelToken
        const last = pages[pages.length - 1]
        if (last && last.checklist === checklist && last.title === title) {
          last.groups.add(group)
          last.stepIds.push(stepId)
        } else {
          pages.push({ checklist, title, groups: new Set([group]), stepIds: [stepId] })
        }
      }
      return pages.map((page, index) => {
        const source = page.groups.size === 1 ? Array.from(page.groups)[0] : undefined
        return {
          kind: 'group',
          id: `${FOCUSED_SYMPTOM_STEP_PREFIX}${symptom.id}${index ? `.${index}` : ''}`,
          titleToken: page.title,
          multiselect: Boolean(source?.multiselect),
          ...(source?.instructionsToken ? { instructionsToken: source.instructionsToken } : {}),
          ...(source?.media ? { media: source.media } : {}),
          ...(source?.noneOption ? { noneOption: source.noneOption } : {}),
          // Without a recorded presence answer, follow the questions' own gates.
          ...(triggered.has(symptom.id) || symptom.selectionOnly
            ? { steps: leaves(page.stepIds).map(withGroupGate) }
            : {
              visibleWhen: { all: [{ conceptId: symptom.conceptId, operator: 'eq', value: symptom.value }] },
              steps: leaves(page.stepIds).map(withGroupGate),
            }),
        }
      })
    }
    const result: GuidedModeStep[] = []
    const renderedLeafIds = new Set(upfrontStepIds.value)
    const answered = new Set(recordedEntryPointIds.value)
    const pagesAfterQuestion: Array<{ questionStepId?: string; pages: GuidedModeStep[] }> = []
    plan.value.waves.forEach((wave, index) => {
      for (const page of wave.pages) {
        const symptom = symptomById.value.get(page.symptomId)
        const stepIds = withCalculatedDateOutputs(
          stepsToAsk(
            page.stepIds.filter(id => !upfrontStepIds.value.has(id)),
            triggered.has(page.symptomId),
          ),
        )
        if (symptom && stepIds.length) {
          const pages = symptomPages(symptom, stepIds)
          if (answered.has(symptom.id)) {
            pagesAfterQuestion.push({ questionStepId: leafIdByConceptId.value.get(symptom.conceptId), pages })
          } else result.push(...pages)
          stepIds.forEach(id => renderedLeafIds.add(id))
        }
      }
      const vitalSignStepIds = withCalculatedDateOutputs(
        stepsToAsk(
          wave.vitalSignStepIds.filter(id => !upfrontStepIds.value.has(id)),
          wave.symptomIds.length > 0 && wave.symptomIds.every(symptomId => triggered.has(symptomId)),
        ),
      )
      if (vitalSignStepIds.length) {
        result.push({
          kind: 'group',
          id: `focused.vital_signs.${index}`,
          titleToken: VITAL_SIGNS_TITLE_TOKEN,
          multiselect: false,
          steps: leaves(vitalSignStepIds),
        })
        vitalSignStepIds.forEach(id => renderedLeafIds.add(id))
      }
    })
    const retained = store.introducedFocusedStepIds.filter(leafAnswered)
    // Answered steps are never routed to, so a rule-recorded answer reaches the summary unasked.
    const recordedByRule = Object.entries(store.assumedSymptomAnswers)
      .flatMap(([symptomId, assumption]) => {
        const conceptId = assumption.conceptId || symptomById.value.get(symptomId)?.conceptId
        const stepId = conceptId ? leafIdByConceptId.value.get(conceptId) : undefined
        return stepId && leafAnswered(stepId) ? [stepId] : []
      })
    const forced = new Set(withCalculatedDateOutputs([
      ...mustConfirm, ...retained, ...answeredWaveStepIds, ...recordedByRule,
    ]))
    const remaining = new Set(withCalculatedDateOutputs(plan.value.remainingStepIds))
    // Retained answers keep their track position instead of moving ahead of every open question.
    for (const step of assessmentSteps.value) {
      if (step.kind === 'group') {
        const nested = (step.steps || [])
          .filter(leaf => leaf.id && !renderedLeafIds.has(leaf.id)
            && (forced.has(leaf.id) || remaining.has(leaf.id)))
        const hasForced = nested.some(leaf => forced.has(leaf.id!))
        if (nested.length) {
          result.push({
            ...step,
            visibleWhen: hasForced ? undefined : step.visibleWhen,
            steps: hasForced ? nested.map(leaf => (forced.has(leaf.id!)
              ? { ...leaf, visibleWhen: undefined } : withGroupGate(leaf))) : nested,
          })
        }
      } else if (step.id && !renderedLeafIds.has(step.id)
        && (forced.has(step.id) || remaining.has(step.id))) {
        result.push(forced.has(step.id) ? { ...step, visibleWhen: undefined } : step)
      }
    }
    const notRecorded = (leaf: GuidedModeStep) => {
      const conceptId = leaf.questionId
        ? definition.value!.questions[leaf.questionId]?.conceptId : undefined
      return !conceptId || !recordedConceptIds.value.has(conceptId)
    }
    const upfront = upfrontSteps.value.flatMap(step => {
      if (step.kind !== 'group') return notRecorded(step) ? [step] : []
      const children = (step.steps || []).filter(notRecorded)
      return children.length ? [{ ...step, steps: children }] : []
    })
    result.unshift(...upfront)
    for (const { questionStepId, pages } of pagesAfterQuestion) {
      const index = result.findIndex(step => (step.kind === 'group' ? step.steps || [] : [step])
        .some(leaf => leaf.id === questionStepId))
      result.splice(index < 0 ? result.length : index + 1, 0, ...pages)
    }
    return result
  })

  const pageLeafIds = (step: GuidedModeStep): string[] => step.kind === 'group'
    ? (step.steps || []).flatMap(pageLeafIds) : step.id ? [step.id] : []
  const steps = computed<GuidedModeStep[] | null>(() => {
    const planned = plannedSteps.value
    if (!planned || !store.focusedPresentedPages.length || !definition.value) return planned
    const activeLeaves = new Set(planned.flatMap(pageLeafIds))
    const projected = new Map<string, GuidedModeStep>()
    const assigned = new Set<string>()
    const recorded = recordedConceptIds.value
    for (const page of store.focusedPresentedPages) {
      const leaves = page.leafIds.flatMap(id => {
        const leaf = stepById.value.get(id)
        const conceptId = leaf?.questionId ? definition.value!.questions[leaf.questionId]?.conceptId : undefined
        const value = conceptId ? answers.value[conceptId] : undefined
        const answered = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ''
        const group = groupByStepId.value.get(id)
        if (!leaf || (conceptId && recorded.has(conceptId)) || assigned.has(id) || (!activeLeaves.has(id) && !answered)
          || !isStepVisible(leaf, answers.value) || (group && !isStepVisible(group, answers.value))) return []
        assigned.add(id)
        return [leaf]
      })
      if (!leaves.length) continue
      if (page.kind === 'question') projected.set(page.id, leaves[0])
      else {
        const source = definition.value.assessment?.steps?.find(step => step.id === page.sourceGroupId)
        projected.set(page.id, {
          ...source, kind: 'group', id: page.id, titleToken: page.titleToken,
          multiselect: page.multiselect, visibleWhen: undefined, steps: leaves,
        })
      }
    }
    const result: GuidedModeStep[] = []
    const emitted = new Set<string>()
    for (const step of planned) {
      const ids = new Set(pageLeafIds(step))
      for (const page of store.focusedPresentedPages) {
        if (!emitted.has(page.id) && projected.has(page.id) && page.leafIds.some(id => ids.has(id))) {
          result.push(projected.get(page.id)!)
          emitted.add(page.id)
        }
      }
      if (step.kind === 'group') {
        const remaining = (step.steps || []).filter(leaf => !assigned.has(leaf.id!))
        if (remaining.length) result.push({ ...step, steps: remaining })
      } else if (!assigned.has(step.id!)) result.push(step)
    }
    for (const [id, step] of projected) if (!emitted.has(id)) result.push(step)
    return result
  })

  function rememberPage(step: GuidedModeStep): string {
    const key = stepKey(step)
    if (!active.value || store.focusedPresentedPages.some(page => page.id === key)) return key
    const ids = pageLeafIds(step)
    const page: GuidedFocusedPage = {
      id: step.kind === 'group' ? `focused.page.${store.focusedPresentedPages.length}` : key,
      sourceStepKey: key, leafIds: ids, kind: step.kind === 'group' ? 'group' : 'question',
    }
    if (step.kind === 'group') {
      page.titleToken = step.titleToken
      page.multiselect = step.multiselect
      const original = definition.value?.assessment?.steps?.find(candidate => candidate.id === step.id)
      const firstGroup = groupByStepId.value.get(ids[0])
      const source = original || (!key.startsWith('focused.vital_signs.')
        && ids.every(id => groupByStepId.value.get(id) === firstGroup) ? firstGroup : undefined)
      if (source?.id) page.sourceGroupId = source.id
    }
    store.focusedPresentedPages = [...store.focusedPresentedPages, page]
    return page.id
  }

  const originFindingsByOption = computed(() => {
    const result: Record<string, string[]> = {}
    const add = (owner: string, code: string) => {
      const options = owner.startsWith('mrx:') ? [owner.slice(4)] : symptomById.value.get(owner)?.focusedOptions || []
      for (const option of options) result[option] = [...new Set([...(result[option] || []), code])]
    }
    for (const [id, owners] of Object.entries(store.introducedFocusedStepOrigins)) {
      const code = planLeafEntries.value.find(leaf => leaf.id === id)?.code
      if (code) owners.forEach(owner => add(owner, code))
    }
    for (const [id, assumption] of Object.entries(store.assumedSymptomAnswers)) {
      const code = assumption.code || symptomById.value.get(id)?.code
      if (code) add(id, code)
    }
    return result
  })

  const startedConceptIds = computed(() => (plan.value?.startedSymptomIds || [])
    .flatMap(id => {
      const symptom = symptomById.value.get(id)
      return symptom && !symptom.selectionOnly ? [symptom.conceptId] : []
    }))
  const startedFocusedOptions = computed(() => Array.from(new Set([
    ...selectedSymptomIds.value,
    ...(plan.value?.startedSymptomIds || []),
  ]
    .flatMap(id => symptomById.value.get(id)?.focusedOptions || []))))

  const evaluationKey = () => JSON.stringify([
    activeAssessmentPath.value, Object.entries(answers.value).sort(([left], [right]) => left.localeCompare(right)),
    selectedSymptomIds.value, store.committedAssumeSymptomIds, store.committedAskSymptomIds,
    store.confirmedAskSymptomIds, originFindingsByOption.value,
  ])

  async function selectedOptions(path: GuidedAssessmentPath, currentDefinition: GuidedModeDefinition,
    currentAnswers: Record<string, GuidedAnswer>): Promise<string[]> {
    const classifications = [
      { type: 'classification' as const, name: 'patient', value: path },
      { type: 'classification' as const, name: 'audience', value: 'chw' },
      { type: 'classification' as const, name: 'experience', value: 'pwa' },
    ]
    const result = await selectGuidedFocusedOptions({
      definition: currentDefinition, answers: currentAnswers, classifications,
      explicitFocusedOptions: [...startedFocusedOptions.value],
      originFindingsByOption: originFindingsByOption.value, allowEmptyOptions: true,
    })
    return result.focusedSelections
  }

  // Resolves true when the focused steps come from MRX, false when they fell back to every step.
  async function evaluate({ commitRules = true, triggerStepId }: FocusedEvaluationOptions = {}): Promise<boolean> {
    const currentDefinition = definition.value
    const path = activeAssessmentPath.value
    if (!active.value || !currentDefinition || !path) return false
    if (commitRules) entryPointRules.commit()
    const currentAnswers = { ...answers.value }
    const key = evaluationKey()
    if (key === evaluatedKey && store.focusedStepIds) return true
    const run = ++evaluationRun
    const allStepIds = leafSteps(currentDefinition).flatMap(step => step.id || [])
    const previousFocusedStepIds = store.focusedStepIds
    evaluating.value = true
    try {
      const selections = await selectedOptions(path, currentDefinition, currentAnswers)
      const result = await mrxEngine().focusedEvaluationFlexi({
        classifications: { patient: path, audience: 'chw', experience: 'pwa' },
        streamlinedSelections: selections,
      }) as FocusedEvaluationResult | null
      if (!result?.comprehensive && !Array.isArray(result?.panels)) {
        throw new Error('Unable to evaluate the focused assessment')
      }
      if (run !== evaluationRun || definition.value !== currentDefinition || key !== evaluationKey()) return false
      let nextFocusedStepIds = result.comprehensive
        ? allStepIds
        : selectFocusedSteps(currentDefinition as PlanDefinition, result.panels!).stepIds
      const memberships: Record<string, string[]> = {}
      const childStepIds: string[] = []
      const vitalStepIds: string[] = []
      const requiredStepIds: string[] = []
      if (!escalated?.value && (previousFocusedStepIds !== null || store.focusedBaselineStepIds !== null)) {
        if (membershipDefinition !== currentDefinition || membershipContent !== store.clinicalContent) {
          optionMemberships.clear()
          membershipDefinition = currentDefinition
          membershipContent = store.clinicalContent
        }
        const optionSteps = (option: string) => {
          const cacheKey = `${path}:${option}`
          if (!optionMemberships.has(cacheKey)) optionMemberships.set(cacheKey, (async () => {
            const scope = await mrxEngine().focusedEvaluationFlexi({
              classifications: { patient: path, audience: 'chw', experience: 'pwa' }, streamlinedSelections: [option],
            }) as FocusedEvaluationResult
            if (scope.comprehensive) return []
            if (!Array.isArray(scope.panels)) throw new Error('Unable to resolve focused origin')
            return selectFocusedSteps(currentDefinition as PlanDefinition, scope.panels).stepIds
          })().catch(error => { optionMemberships.delete(cacheKey); throw error }))
          return optionMemberships.get(cacheKey)!
        }
        const owners = new Map<string, GuidedModeSymptom[]>()
        for (const id of selectedSymptomIds.value) {
          const symptom = symptomById.value.get(id)
          if (symptom && (!store.recordedSymptomIds.includes(id) || activeAssumeIds.value.includes(id)
            || activeAskIds.value.includes(id))) owners.set(id, [symptom])
        }
        const symptomOptions = new Set(startedFocusedOptions.value)
        for (const option of selections) {
          if (!symptomOptions.has(option) && !(store.focusedBaselineSelections || []).includes(option)) {
            owners.set(`mrx:${option}`, planSymptoms.value.filter(symptom => symptom.focusedOptions.includes(option)))
          }
        }
        for (const [owner, symptoms] of owners) {
          const options = owner.startsWith('mrx:') ? [owner.slice(4)]
            : [...new Set(symptoms.flatMap(symptom => symptom.focusedOptions))]
          const ids = (await Promise.all(options.map(optionSteps))).flat()
          const detail = buildFocusedPlan({
            definition: currentDefinition as PlanDefinition, symptoms,
            selectedSymptomIds: symptoms.map(symptom => symptom.id), values: values.value,
            focusedStepIds: [], vitalSignRules: [],
          })
          const children = detail.waves.flatMap(wave => wave.pages.flatMap(page => page.stepIds))
          const vitals = detail.waves.flatMap(wave => wave.vitalSignStepIds)
          memberships[owner] = [...new Set([...ids, ...children, ...vitals])]
          requiredStepIds.push(...children, ...vitals)
          if (!store.focusedActiveOwners.includes(owner)) {
            childStepIds.push(...children)
            vitalStepIds.push(...vitals)
          }
        }
        nextFocusedStepIds = [...new Set([...nextFocusedStepIds, ...requiredStepIds])]
      }
      if (run !== evaluationRun || definition.value !== currentDefinition || key !== evaluationKey()) return false
      store.focusedStepIds = nextFocusedStepIds
      // Escalation adds every question; none of them was introduced by a started symptom.
      entryPointRules.recordIntroducedSteps(
        plan.value,
        escalated?.value ? null : previousFocusedStepIds,
        nextFocusedStepIds,
        memberships, childStepIds, vitalStepIds, triggerStepId,
      )
      if (store.focusedBaselineSelections === null) store.focusedBaselineSelections = [...selections]
      evaluatedKey = evaluationKey()
      return true
    } catch (error) {
      if (run !== evaluationRun || definition.value !== currentDefinition || key !== evaluationKey()) return false
      reportError?.(error)
      evaluatedKey = null
      store.focusedStepIds = allStepIds
      entryPointRules.recordIntroducedSteps(
        plan.value,
        escalated?.value ? null : previousFocusedStepIds,
        allStepIds,
      )
      return false
    } finally {
      if (run === evaluationRun) evaluating.value = false
    }
  }

  function cancelEvaluation() {
    evaluationRun += 1
    evaluating.value = false
  }

  // Symptoms evaluates on Next itself, after the escalation check.
  watch(active, isActive => {
    if (isActive && store.focusedStepIds === null && !evaluating.value && store.view !== 'symptoms') void evaluate()
  }, { immediate: true })

  function reset() {
    cancelEvaluation()
    evaluatedKey = null
    optionMemberships.clear()
    store.focusedStepIds = null
    entryPointRules.reset()
  }

  return {
    active, evaluating, plan, steps, startedConceptIds, startedFocusedOptions, evaluate,
    rememberPage, originFindingsByOption, askStepIds,
    commitEntryPointRules: entryPointRules.commit, cancelEvaluation, reset,
    discardInvalidConcepts: entryPointRules.discardInvalidConcepts,
  }
}
